import * as Sentry from '@sentry/nextjs';
import { USE_SENTRY } from '@/enviroment';
import { EXCEPTION_LEVELS, SHOW_SENTRY_ERROR_MODAL } from '../sentry.keys';
import type { SentryExceptionOptions } from '../sentry.types';

// ============================================================================
// 💬 User Feedback Utilities
// ============================================================================

/**
 * 💬 Show Sentry crash report dialog for user feedback
 * Captures the exception and displays a feedback dialog to collect user input
 *
 * @param error - The error that occurred
 * @param options - Optional exception options (tags, contexts, level)
 * @returns The Sentry event ID if captured
 *
 * @example
 * ```ts
 * showCrashReportDialog(error, {
 *   tags: {
 *     errorBoundary: 'tenant-route',
 *     tenant: 'acme'
 *   }
 * });
 * ```
 */
export const showCrashReportDialog = (
  error: Error | unknown,
  options?: SentryExceptionOptions,
): string | undefined => {
  if (!USE_SENTRY || !SHOW_SENTRY_ERROR_MODAL) {
    return undefined;
  }

  const captureOptions: Record<string, any> = {
    level: options?.level ?? EXCEPTION_LEVELS.ERROR,
  };

  if (options?.tags) {
    captureOptions.tags = options.tags;
  }

  if (options?.contexts) {
    captureOptions.contexts = options.contexts;
  }

  const eventId = Sentry.captureException(error, captureOptions);

  if (eventId && 'showReportDialog' in Sentry) {
    Sentry.showReportDialog({
      eventId,
      title: 'It looks like we encountered an error',
      subtitle: 'Our team has been notified',
      subtitle2: 'If you would like to help, tell us what happened below',
      labelName: 'Name',
      labelEmail: 'Email',
      labelComments: 'What happened?',
      labelSubmit: 'Submit Feedback',
      successMessage: 'Thanks for your feedback! We will look into this.',
    });
  }

  return eventId;
};
